import AwsLogo from "../icons/logos/aws.svg";
import AzureLogo from "../icons/logos/Azure.png";
import AzureSqlLogo from "../icons/logos/azuresql.jpeg";
import DatabricksLogo from "../icons/logos/databricks.png";
import ExcelLogo from "../icons/logos/excel.png";
import GcpLogo from "../icons/logos/gcp.png";
import GoogleSheetsLogo from "../icons/logos/googlesheets.png";
import HubspotLogo from "../icons/logos/hubspot.jpg";
import LookerLogo from "../icons/logos/looker.png";
import PowerBiLogo from "../icons/logos/powerBI.jpeg";
import SalesforceLogo from "../icons/logos/salesforce.png";
import SapLogo from "../icons/logos/sap.jpeg";
import SnowflakeLogo from "../icons/logos/snowflake.png";
import TableauLogo from "../icons/logos/tableau.png";
import XeroLogo from "../icons/logos/xero.jpeg";

export const specialistSolutions = [
  {
    title: "Financial Services & Lending Data",
    href: "/domain-expertise/financial-services",
    copy: "Core banking, loan origination, servicing, payments and CRM data connected into governed reporting foundations.",
  },
  {
    title: "Sustainability Reporting Data",
    href: "/domain-expertise/sustainability-reporting",
    copy: "Utility, procurement, finance, ERP and site data modelled for NGER, NPI, Scope 1, Scope 2 and Scope 3 reporting inputs.",
  },
  {
    title: "Industrial Operations Data",
    href: "/domain-expertise/construction",
    copy: "Construction, quarry, mining, logistics, project and field operations data connected for controlled delivery reporting.",
  },
  {
    title: "Energy Data",
    href: "/domain-expertise/energy",
    copy: "Meter, asset, operational, finance and emissions data connected for reliable energy and sustainability reporting.",
  },
  {
    title: "Governed Analytics Platforms",
    href: "/services/data-engineering",
    copy: "Azure, Fabric, Synapse, Snowflake and Databricks foundations with quality controls, lineage and semantic layers.",
  },
];

export const domainExpertise = [
  {
    id: "finance",
    title: "Financial Services & Lending Data Platforms",
    tabLabel: "Financial Services",
    href: "/domain-expertise/financial-services",
    subtitle: "Connecting legacy core banking engines, modern origination workflows, and CRM applications.",
    summary: "In highly regulated environments, data pipelines often break across the loan lifecycle. We eliminate operational blind spots by engineering comprehensive data lineage that tracks an asset from initial credit evaluation to live ledger processing.",
    copy: "Core banking, origination, CRM, payments and transaction data engineered into governed lending lifecycle reporting assets.",
    points: [
      "Lead-to-Settlement Processing: We map and unify the entire lifecycle from top-of-funnel lead generation through risk assessment, formal settlement and portfolio servicing.",
      "Core Platform Migrations: Managing schema mappings, data transformations and historical validation checks to reduce data loss risk during vendor transitions.",
      "Automated Clearing House Integrations: Deploying serverless automation with PGP encryption and secure SFTP file delivery to replace manual bank settlement handling.",
    ],
    tech: ["Microsoft Fabric", "Azure Synapse", "PGP Encryption", "Secure SFTP", "Power BI", "Snowflake"],
  },
  {
    id: "sustainability",
    title: "Sustainability & ESG Foundations",
    tabLabel: "Sustainability Reporting",
    href: "/domain-expertise/sustainability-reporting",
    subtitle: "Turn unstructured operational records and disjointed registers into audit-ready ESG data assets.",
    summary: "Sustainability compliance data is notoriously scattered. We build reliable, repeatable data pipelines that feed cleanly into reporting frameworks, emissions models and sustainability scoring workflows.",
    copy: "NGER, NPI, Scope 1, Scope 2 and Scope 3 reporting inputs structured from utility, procurement, finance and operational data.",
    points: [
      "Automated NGER Inputs: Streamlining National Greenhouse and Energy Reporting inputs through governed pipelines for utility meter, fuel and consumption data.",
      "Ecosystem Platform Integration: Connecting supply chain, ERP, finance, procurement and local spreadsheet data into centralised cloud environments.",
      "Audit-Ready Accountability: Designing explicit data contracts and lineage tracking so carbon and sustainability metrics can be traced back to source ingestion points.",
    ],
    tech: ["Microsoft Sustainability Manager", "Cloud for Sustainability", "SharePoint Registers", "Utility Meter APIs", "Power BI ESG Dashboards"],
  },
  {
    id: "construction",
    title: "Industrial Operations Data Foundations",
    tabLabel: "Industrial Operations",
    href: "/domain-expertise/construction",
    subtitle: "Connecting project controls, procurement, subcontractor, finance, site, asset and industrial operations records.",
    summary: "Industrial reporting breaks down when project delivery data, commercial controls and site-level operating records sit in separate systems. We build governed data foundations that connect physical activity with finance, procurement and operating context.",
    copy: "Construction, quarry, mining, logistics, project, procurement, finance and site data connected for clearer operating decisions.",
    points: [
      "Project Controls Reporting: Connecting schedules, progress, cost codes and delivery milestones into controlled project reporting models.",
      "Commercial and Procurement Reconciliation: Linking budgets, purchase orders, subcontractor data and ERP finance records so teams can explain cost variance.",
      "Site and Sustainability Registers: Structuring utility, fuel, waste, water, asset and contractor records for operational and sustainability reporting.",
    ],
    tech: ["Power BI", "Microsoft Fabric", "Azure SQL", "SharePoint", "ERP Integrations", "REST APIs"],
  },
  {
    id: "energy",
    title: "Energy Data Foundations",
    tabLabel: "Energy",
    href: "/domain-expertise/energy",
    subtitle: "Connecting meter, asset, operational, supplier and finance data for trusted energy reporting.",
    summary: "Energy reporting relies on data from meters, invoices, assets, suppliers and operational systems. We consolidate those sources into governed datasets for performance visibility, cost analysis and emissions reporting inputs.",
    copy: "Meter, asset, operational, finance and emissions data consolidated for trusted energy reporting and performance visibility.",
    points: [
      "Meter-to-Reporting Lineage: Validating utility meter and interval consumption data so reported usage can be traced back to source records.",
      "Asset and Operations Context: Connecting sites, equipment, maintenance records and operational data to explain what drives energy performance.",
      "Energy and Emissions Inputs: Structuring electricity, fuel, supplier and finance data for NGER, Scope 1, Scope 2 and Scope 3 reporting workflows.",
    ],
    tech: ["Utility Meter APIs", "Azure Data Factory", "Microsoft Fabric", "Power BI", "NGER Data Models", "Supplier Files"],
  },
];

export const homepageProblems = [
  "Reports disagree because finance, CRM, operational and spreadsheet data are not reconciled",
  "Teams spend hours checking numbers manually before leaders can act",
  "Critical reporting depends on people, files and workarounds instead of controlled pipelines",
  "Audit, sustainability and migration work is harder because lineage and ownership are unclear",
];

export const deliverySteps = [
  {
    step: "01",
    title: "Assess",
    copy: "Map systems, reporting pain points, data quality risks and business priorities.",
  },
  {
    step: "02",
    title: "Design",
    copy: "Define the target architecture, integration plan, controls and data model.",
  },
  {
    step: "03",
    title: "Build",
    copy: "Deliver pipelines, models, automation workflows, reconciliation checks and governance.",
  },
  {
    step: "04",
    title: "Enable",
    copy: "Document, hand over and support the team so the platform keeps improving.",
  },
];

export const proofPoints = [
  "Delivered an Azure-based enterprise data foundation integrating core banking, loan origination, CRM and operational data.",
  "Automated secure payment processing workflows using Azure Functions, encryption, SFTP and database integration.",
  "Designed sustainability reporting architecture across Microsoft Sustainability Manager, Azure Synapse, PostgreSQL and Snowflake.",
  "Consolidated 40+ sustainability data sources and reduced recurring analysis effort through governed data foundations.",
];

export const businessOutcomes = [
  "Faster operational, finance and management reporting cycles",
  "Fewer manual extracts, spreadsheet reconciliations and workflow handoffs",
  "One trusted foundation for customer, asset, finance and operational data",
  "Audit-ready compliance, sustainability and evidence trails",
  "Cleaner semantic models, governed metrics and fewer duplicated reports",
  "Better visibility across customer, asset, emissions and transaction workflows",
];

export const services = [
  {
    title: "Data Strategy & Platform Review",
    href: "/services#advisory",
    copy: "Assess systems, data flows, operational bottlenecks, reporting risks and automation opportunities before committing delivery spend.",
  },
  {
    title: "Data Platforms & Pipelines",
    href: "/services#data-engineering",
    copy: "Build governed ingestion, transformation, modelling and reconciliation foundations that connect business systems.",
  },
  {
    title: "Operational Integration & Automation",
    href: "/services#automation-ai",
    copy: "Connect CRMs, finance systems, ERPs, field systems, spreadsheets and APIs into automated workflows.",
  },
  {
    title: "Semantic BI & Decision Intelligence",
    href: "/services#data-engineering",
    copy: "Create governed metrics, semantic models, dashboards and decision-ready insight for leaders and teams.",
  },
];

export const homepageOutcomeChoices = [
  {
    number: "01",
    title: "Review",
    copy: "Understand what is broken, risky or inefficient across your systems, data flows and operational setup.",
  },
  {
    number: "02",
    title: "Build",
    copy: "Create the integrations, data platform, pipelines and models your business can trust.",
  },
  {
    number: "03",
    title: "Automate",
    copy: "Remove manual work from spreadsheets, emails, extracts, approvals and recurring operational workflows.",
  },
  {
    number: "04",
    title: "Operate",
    copy: "Access ongoing senior data capability without building a full internal data team.",
  },
];

export const engagementModels = [
  {
    number: "01",
    title: "Fixed-Scope Review",
    summary: "A short, structured engagement to assess your current state and provide a practical roadmap.",
    items: [
      "Microsoft Fabric and Azure readiness reviews",
      "Operational system, data flow and governance assessments",
      "Semantic BI model audits and reporting risk reviews",
      "Sustainability, finance and operational data readiness checks",
    ],
    fit: "Best when you need a clear recommendation, roadmap or second opinion.",
  },
  {
    number: "02",
    title: "Project Delivery",
    summary: "A defined build engagement with agreed scope, milestones and deliverables.",
    items: [
      "Data platform and pipeline builds",
      "Source system integration and reconciliation controls",
      "Semantic BI reporting layers and metric definitions",
      "Automation workflows across cloud, finance, CRM and operational systems",
    ],
    fit: "Best when you already know the business outcome and need delivery ownership.",
  },
  {
    number: "03",
    title: "Monthly Data Partner",
    summary: "Ongoing data engineering, integration, reporting and platform support for continuous improvement.",
    items: [
      "Pipeline monitoring, improvement and support",
      "Monthly operational data, reporting and semantic layer enhancements",
      "Integration backlog delivery across business systems",
      "Advisory support for CFO, CIO, operations and reporting teams",
    ],
    fit: "Best when you need a reliable data engineering partner without long lock-in.",
    featured: true,
  },
];

export const financeCapabilities = [
  "Core banking data foundations",
  "Loan origination and servicing reporting",
  "Lead-to-settlement visibility",
  "Direct debit and direct credit automation",
  "Transaction feedback and exception handling",
  "Migration mapping and enrichment",
  "Reconciliation and audit controls",
  "CRM and operational reporting",
];

export const esgCapabilities = [
  {
    title: "Supply Chain & Scope 3 Data Hardening",
    copy: "The hardest sustainability data often sits outside your corporate walls. We engineer pipelines that capture, clean and validate supplier, logistics and procurement data.",
  },
  {
    title: "Regulatory Compliance Mapping",
    copy: "We translate NGER, NPI and Scope reporting requirements into governed data models, transformation rules and controlled reporting outputs.",
  },
  {
    title: "Sub-Ledger Data Harmonisation",
    copy: "Cross-reference energy usage, fuel allocations, procurement records and finance ledgers so anomalies are detected before final reporting cycles.",
  },
  {
    title: "Microsoft Sustainability Platform Readiness",
    copy: "Prepare source data, reference mappings and reporting layers for Microsoft Sustainability Manager, Cloud for Sustainability and Power BI.",
  },
];

export const integrationCategories = [
  {
    title: "Financial Services & Lending",
    copy: "Core banking, origination, servicing and payment data connected for lending lifecycle visibility.",
    systems: [
      { name: "Core banking", initials: "CB" },
      { name: "Loan origination", initials: "LOS" },
      { name: "Loan servicing", initials: "LS" },
      { name: "Credit checks", initials: "CR" },
      { name: "Settlements", initials: "SET" },
      { name: "Direct debit", initials: "DD" },
      { name: "Direct credit", initials: "DC" },
      { name: "Transaction files", initials: "TXN" },
      { name: "Reconciliation", initials: "REC" },
      { name: "HubSpot", logo: HubspotLogo, wide: true },
      { name: "SFTP", initials: "SFTP" },
    ],
  },
  {
    title: "Cloud & Warehouses",
    copy: "Platforms for scalable storage, transformation, modelling and governed analytics.",
    systems: [
      { name: "Azure", logo: AzureLogo },
      { name: "AWS", logo: AwsLogo },
      { name: "GCP", logo: GcpLogo },
      { name: "Snowflake", logo: SnowflakeLogo, wide: true },
      { name: "Databricks", logo: DatabricksLogo, wide: true },
      { name: "Azure SQL", logo: AzureSqlLogo },
      { name: "Microsoft Fabric", initials: "FAB" },
      { name: "Synapse", initials: "SYN" },
      { name: "SQL Server", initials: "SQL" },
      { name: "PostgreSQL", initials: "PG" },
      { name: "BigQuery", initials: "BQ" },
    ],
  },
  {
    title: "Analytics & Reporting",
    copy: "Reporting layers, semantic models and self-service analytics for decision makers.",
    systems: [
      { name: "Power BI", logo: PowerBiLogo, wide: true, featured: true },
      { name: "Tableau", logo: TableauLogo, wide: true },
      { name: "Looker", logo: LookerLogo, wide: true },
      { name: "Excel", logo: ExcelLogo },
      { name: "Google Sheets", logo: GoogleSheetsLogo },
      { name: "Power Query", initials: "PQ" },
      { name: "Paginated Reports", initials: "PR" },
    ],
  },
  {
    title: "Business Applications",
    copy: "CRM, sales, marketing and operational platforms centralised into trusted models.",
    systems: [
      { name: "Salesforce", logo: SalesforceLogo, wide: true },
      { name: "HubSpot", logo: HubspotLogo, wide: true },
      { name: "Dynamics 365", initials: "D365" },
      { name: "Zoho CRM", initials: "ZO" },
      { name: "Pipedrive", initials: "PD" },
      { name: "GA4 analytics", initials: "GA4" },
      { name: "LinkedIn Ads", initials: "LI" },
    ],
  },
  {
    title: "Finance, ERP & Commerce",
    copy: "Finance, accounting, ERP and commerce data connected for reporting and controls.",
    systems: [
      { name: "SAP", logo: SapLogo, wide: true },
      { name: "Xero", logo: XeroLogo },
      { name: "MYOB", initials: "MYOB" },
      { name: "QuickBooks", initials: "QB" },
      { name: "NetSuite", initials: "NS" },
      { name: "Dynamics Finance", initials: "D365" },
      { name: "Stripe", initials: "ST" },
      { name: "Shopify", initials: "SH" },
    ],
  },
  {
    title: "Automation & Data Movement",
    copy: "Pipelines, APIs and workflow tools that make data movement reliable and observable.",
    systems: [
      { name: "REST APIs", initials: "API" },
      { name: "GraphQL", initials: "GQL" },
      { name: "SFTP", initials: "SFTP" },
      { name: "Azure Function App", initials: "FUNC" },
      { name: "Synapse Pipelines", initials: "SYN" },
      { name: "Stored Procedures", initials: "PROC" },
      { name: "Control Tables", initials: "CTL" },
      { name: "SharePoint", initials: "SP" },
      { name: "OneDrive", initials: "OD" },
      { name: "Power Automate", initials: "PA" },
      { name: "Azure Data Factory", initials: "ADF" },
      { name: "dbt", initials: "dbt" },
      { name: "Airbyte", initials: "AB" },
      { name: "Fivetran", initials: "F5" },
    ],
  },
];
